import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Store, Star, TrendingUp, Users, Search, Check, DollarSign, Zap, Flame, Sparkles, Shield, X, ChevronDown, ChevronUp, Award, Bot, Loader2, AlertTriangle, Upload, Trash2, ShieldCheck } from 'lucide-react';
import { Card, CardHeader, Spinner, InfoTip } from '../components/ui';
import * as api from '../services/api';
import TrackRecordBadge from '../components/TrackRecordBadge';

const TABS = ['Browse', 'My Strategies', 'Creator Dashboard'];
const CATEGORIES = ['All', 'Trend Following', 'Mean Reversion', 'Breakout', 'Scalping', 'AI-Based', 'Grid Trading', 'Market Making'];
const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'roi', label: 'Best ROI' },
  { value: 'winrate', label: 'Win Rate' },
  { value: 'sharpe', label: 'Sharpe Ratio' },
  { value: 'safe', label: 'Lowest Risk' },
];

const RISK_STYLE = {
  Low: { text: 'text-emerald-400', bg: 'bg-emerald-500/20', bar: 'bg-emerald-400' },
  Medium: { text: 'text-amber-400', bg: 'bg-amber-500/20', bar: 'bg-amber-400' },
  High: { text: 'text-red-400', bg: 'bg-red-500/20', bar: 'bg-red-400' },
};

const BADGE_CONFIG = {
  hot:     { icon: Flame,    label: 'HOT',     cls: 'bg-orange-500/20 text-orange-400 border border-orange-500/30' },
  popular: { icon: TrendingUp, label: 'TOP',   cls: 'bg-blue-500/20 text-blue-400 border border-blue-500/30' },
  new:     { icon: Sparkles, label: 'NEW',     cls: 'bg-violet-500/20 text-violet-400 border border-violet-500/30' },
  native:  { icon: Award,    label: 'NATIVE',  cls: 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30' },
};

// Deterministic sparkline — no Math.random
function Sparkline({ id, roi, risk, width = 88, height = 30 }) {
  const seed = id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const pts = Array.from({ length: 18 }, (_, i) => {
    const trend = (roi / 100) * (i / 17);
    const wave = Math.sin((i + seed) * 0.9) * (risk === 'High' ? 0.06 : risk === 'Low' ? 0.015 : 0.035);
    return Math.max(0, trend + wave);
  });
  const min = Math.min(...pts), max = Math.max(...pts);
  const norm = v => height - 2 - ((v - min) / (max - min + 0.001)) * (height - 4);
  const step = width / 17;
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${norm(p).toFixed(1)}`).join(' ');
  const color = risk === 'High' ? '#f87171' : risk === 'Low' ? '#34d399' : '#fbbf24';
  return (
    <svg width={width} height={height} className="overflow-visible">
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BadgePill({ badge }) {
  if (!badge || !BADGE_CONFIG[badge]) return null;
  const { icon: Icon, label, cls } = BADGE_CONFIG[badge];
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full font-semibold ${cls}`}>
      <Icon className="w-2.5 h-2.5" />{label}
    </span>
  );
}

function StarRating({ avg, count }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} className={`w-3 h-3 ${i < Math.round(avg) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
      ))}
      <span className="text-xs text-gray-400 ml-0.5">{avg} ({count})</span>
    </div>
  );
}

function AuditBadge({ status }) {
  if (status === 'audited') return <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><Shield className="w-3 h-3" />Audited</span>;
  if (status === 'pending') return <span className="inline-flex items-center gap-1 text-xs text-amber-400"><Shield className="w-3 h-3" />Audit Pending</span>;
  return null;
}

function MetricBox({ label, value, color = 'text-white' }) {
  return (
    <div className="bg-dark-700/80 rounded-lg p-2 text-center">
      <p className="text-gray-500 text-xs mb-0.5">{label}</p>
      <p className={`font-bold text-sm ${color}`}>{value}</p>
    </div>
  );
}

// Strategy detail drawer
function StrategyDrawer({ strategy, onClose, onDeploy }) {
  if (!strategy) return null;
  const risk = RISK_STYLE[strategy.risk] || RISK_STYLE.Medium;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-dark-800 border-l border-dark-600 overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-dark-800 border-b border-dark-600 p-4 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-bold text-white text-lg">{strategy.name}</h2>
              <BadgePill badge={strategy.badge} />
              <span className={`text-xs px-2 py-0.5 rounded-full ${risk.bg} ${risk.text}`}>{strategy.risk} Risk</span>
            </div>
            <p className="text-gray-400 text-sm mt-0.5">{strategy.category} · {strategy.chain} · {strategy.tokenPair}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg bg-dark-700 hover:bg-dark-600 text-gray-400"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-5 flex-1">
          {/* Description */}
          <p className="text-gray-300 text-sm leading-relaxed">{strategy.description}</p>

          {/* Performance metrics */}
          {strategy.metrics && (
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Performance Metrics</p>
              <div className="grid grid-cols-3 gap-2">
                {strategy.metrics.roi != null && <MetricBox label="ROI" value={`+${strategy.metrics.roi}%`} color="text-emerald-400" />}
                {strategy.metrics.winRate != null && <MetricBox label="Win Rate" value={`${strategy.metrics.winRate}%`} color="text-accent-primary" />}
                {strategy.metrics.maxDrawdown != null && <MetricBox label="Max Drawdown" value={`${strategy.metrics.maxDrawdown}%`} color="text-red-400" />}
                {strategy.metrics.sharpeRatio != null && <MetricBox label="Sharpe" value={strategy.metrics.sharpeRatio} color="text-chart-purple" />}
                {strategy.metrics.calmarRatio != null && <MetricBox label="Calmar" value={strategy.metrics.calmarRatio} color="text-blue-400" />}
                {strategy.metrics.profitFactor != null && <MetricBox label="Profit Factor" value={strategy.metrics.profitFactor} color="text-amber-400" />}
              </div>
            </div>
          )}

          {/* Entry conditions */}
          <div>
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Entry Conditions</p>
            <ul className="space-y-1.5">
              {(strategy.entryConditions || []).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                  {c}
                </li>
              ))}
            </ul>
          </div>

          {/* Exit conditions */}
          <div>
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Exit Conditions</p>
            <ul className="space-y-1.5">
              {(strategy.exitConditions || []).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✕</span>
                  {c}
                </li>
              ))}
            </ul>
          </div>

          {/* Parameters */}
          <div>
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Strategy Parameters</p>
            <div className="bg-dark-700 rounded-xl p-3 grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-xs">
              {Object.entries(strategy.parameters || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2">
                  <span className="text-gray-400">{k}</span>
                  <span className="text-accent-primary font-semibold">{String(v)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Indicators */}
          <div>
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Indicators Used</p>
            <div className="flex flex-wrap gap-1.5">
              {(strategy.indicators || []).map(ind => (
                <span key={ind} className="text-xs bg-dark-600 text-gray-300 px-2.5 py-1 rounded-lg border border-dark-500">{ind}</span>
              ))}
            </div>
          </div>

          {/* Backtest info */}
          {(strategy.backtestMonths != null || strategy.liveMonths != null || strategy.minCapital != null) && (
            <div className="grid grid-cols-3 gap-3 text-center text-xs">
              {strategy.backtestMonths != null && (
                <div className="bg-dark-700 rounded-lg p-2">
                  <p className="text-gray-500">Backtest</p>
                  <p className="text-white font-semibold">{strategy.backtestMonths}mo</p>
                </div>
              )}
              {strategy.liveMonths != null && (
                <div className="bg-dark-700 rounded-lg p-2">
                  <p className="text-gray-500">Live Track</p>
                  <p className="text-white font-semibold">{strategy.liveMonths}mo</p>
                </div>
              )}
              {strategy.minCapital != null && (
                <div className="bg-dark-700 rounded-lg p-2">
                  <p className="text-gray-500">Min Capital</p>
                  <p className="text-white font-semibold">${strategy.minCapital}</p>
                </div>
              )}
            </div>
          )}

          {/* Creator + audit */}
          <div className="flex items-center justify-between bg-dark-700 rounded-xl p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-accent-primary/20 text-accent-primary text-sm flex items-center justify-center font-bold">
                {strategy.creator?.name?.slice(0, 1)}
              </div>
              <div>
                <p className="text-white text-sm font-medium flex items-center gap-1">
                  {strategy.creator?.name}
                  {strategy.creator?.verified && <Check className="w-3.5 h-3.5 text-accent-primary" />}
                </p>
                <p className="text-gray-400 text-xs">{(strategy.subscribers ?? strategy.deployCount ?? 0).toLocaleString()} subscribers</p>
              </div>
            </div>
            <AuditBadge status={strategy.auditStatus} />
          </div>
        </div>

        {/* Deploy footer */}
        <div className="sticky bottom-0 bg-dark-800 border-t border-dark-600 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-sm">Price</span>
            <span className="text-xl font-bold text-emerald-400">FREE</span>
          </div>
          <button onClick={() => onDeploy(strategy)} className="btn-primary w-full flex items-center justify-center gap-2 py-3">
            <Zap className="w-4 h-4" /> Deploy to My Bot
          </button>
        </div>
      </div>
    </div>
  );
}

// Determine if a bot's token pair is compatible with a strategy's tokenPair field
function isPairCompatible(strategyPair, botPair) {
  if (!strategyPair || strategyPair === 'Any') return true;
  if (!botPair) return false;
  const sp = strategyPair.toLowerCase();
  const bp = botPair.toLowerCase();
  if (sp === bp) return true;
  // "Stablecoin Pairs" matches pairs containing usdc/usdt/dai
  if (sp === 'stablecoin pairs') return /usdc|usdt|dai|busd/.test(bp);
  // "High-Volume Pairs" — assume any active pair qualifies
  if (sp === 'high-volume pairs') return true;
  // Check if tokens overlap (e.g. strategy = WETH/USDC, bot = WETH/USDT → partial match)
  const sToks = sp.replace('/', ' ').split(' ');
  const bToks = bp.replace('/', ' ').split(' ');
  return sToks.some(t => bToks.includes(t));
}

function DeployModal({ strategy, onClose }) {
  const [bots, setBots] = useState([]);
  const [loadingBots, setLoadingBots] = useState(true);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [allocation, setAllocation] = useState(20);
  const [deploying, setDeploying] = useState(false);
  const [deployed, setDeployed] = useState(false);
  const [deployError, setDeployError] = useState('');

  useEffect(() => {
    api.listBots()
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setBots(list);
        // Auto-select first compatible bot if any
        const first = list.find(b => isPairCompatible(strategy?.tokenPair, b.pair));
        if (first) setSelectedBotId(first.botId);
        else if (list.length > 0) setSelectedBotId(list[0].botId);
      })
      .catch(() => setBots([]))
      .finally(() => setLoadingBots(false));
  }, [strategy?.tokenPair]);

  const selectedBot = bots.find(b => b.botId === selectedBotId);
  const expectedMonthly = strategy?.metrics?.roi != null
    ? ((allocation / 100) * (strategy.metrics.roi / 12)).toFixed(1)
    : null;

  const compatible = bots.filter(b => isPairCompatible(strategy?.tokenPair, b.pair));
  const other = bots.filter(b => !isPairCompatible(strategy?.tokenPair, b.pair));
  const orderedBots = [...compatible, ...other];

  const confirm = async () => {
    if (!selectedBotId) return;
    setDeploying(true);
    setDeployError('');
    try {
      await api.deployMarketplaceStrategy(strategy.id, { botId: selectedBotId, allocation: allocation / 100 });
      setDeployed(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      setDeployError(err?.message || 'Deployment failed. Please try again.');
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <Card className="w-full max-w-md mx-4 border border-dark-500 max-h-[90vh] overflow-y-auto">
        {deployed ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-white font-bold text-lg">Strategy Activated!</p>
            <p className="text-gray-400 text-sm mt-1">
              {strategy?.name} is now assigned to <span className="text-accent-primary">{selectedBot?.name || selectedBotId}</span>.
            </p>
          </div>
        ) : (
          <>
            <CardHeader title={`Deploy: ${strategy?.name}`} subtitle="Select a bot — compatible ones are highlighted" />
            <div className="space-y-4 mt-2">

              {/* Bot selector */}
              <div>
                <label className="text-gray-400 text-sm mb-2 block">Target Bot</label>
                {loadingBots ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                    <Spinner size="sm" className="text-accent-primary" /> Loading your bots...
                  </div>
                ) : orderedBots.length === 0 ? (
                  <div className="bg-dark-700 border border-dark-500 rounded-xl p-3 text-sm text-gray-400">
                    No bots found. <a href="/bots/new" className="text-accent-primary hover:underline">Create a bot first</a> to deploy a strategy.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {orderedBots.map(bot => {
                      const isCompat = isPairCompatible(strategy?.tokenPair, bot.pair);
                      const isSelected = bot.botId === selectedBotId;
                      return (
                        <button
                          key={bot.botId}
                          onClick={() => setSelectedBotId(bot.botId)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                            isSelected
                              ? 'border-accent-primary bg-accent-primary/10'
                              : 'border-dark-500 bg-dark-700 hover:border-dark-400'
                          }`}
                        >
                          {/* Status dot */}
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${bot.status === 'active' ? 'bg-emerald-400' : 'bg-gray-500'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{bot.name || bot.botId}</p>
                            <p className="text-xs text-gray-500">{bot.pair || 'No pair set'} · {bot.network || 'Unknown network'}</p>
                          </div>
                          {isCompat && (
                            <span className="text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full flex-shrink-0">
                              Compatible
                            </span>
                          )}
                          {isSelected && <Check className="w-4 h-4 text-accent-primary flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
                {strategy?.tokenPair && strategy.tokenPair !== 'Any' && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    Strategy targets <span className="text-gray-300">{strategy.tokenPair}</span>. Compatible bots are highlighted — others can still run this strategy.
                  </p>
                )}
              </div>

              {/* Allocation slider */}
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-gray-400 text-sm">Capital Allocation</label>
                  <span className="text-accent-primary font-semibold text-sm">{allocation}%</span>
                </div>
                <input type="range" min={1} max={100} value={allocation} onChange={e => setAllocation(+e.target.value)} className="w-full" />
                <div className="flex justify-between text-xs text-gray-500 mt-1"><span>1%</span><span>100%</span></div>
              </div>

              {/* Summary */}
              <div className="bg-dark-700 rounded-xl p-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Strategy</span><span className="text-white font-medium">{strategy?.name}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Bot</span><span className="text-white font-medium">{selectedBot?.name || selectedBotId || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Risk Level</span><span className={RISK_STYLE[strategy?.risk]?.text}>{strategy?.risk}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Backtest ROI</span><span className="text-emerald-400 font-bold">{strategy?.metrics?.roi != null ? `+${strategy.metrics.roi}%` : '—'}</span></div>
                {expectedMonthly != null && (
                  <div className="flex justify-between border-t border-dark-500 pt-2 mt-2">
                    <span className="text-gray-400">Est. monthly return</span>
                    <span className="text-emerald-400 font-bold">+{expectedMonthly}% of allocation</span>
                  </div>
                )}
              </div>

              {deployError && (
                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />{deployError}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={confirm}
                  disabled={deploying || !selectedBotId || loadingBots}
                  className="btn-primary flex-1 flex items-center justify-center gap-2 py-2.5"
                >
                  {deploying ? <Spinner size="sm" className="text-white" /> : <Zap className="w-4 h-4" />}
                  {deploying ? 'Activating...' : 'Activate Now'}
                </button>
                <button onClick={onClose} className="px-4 py-2.5 rounded-lg bg-dark-700 text-gray-400 hover:text-white text-sm transition-colors">Cancel</button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function StrategyCard({ strategy, onDeploy, onDetail, selected, onSelect }) {
  const risk = RISK_STYLE[strategy.risk] || RISK_STYLE.Medium;
  const m = strategy.metrics || {};
  const hasMetrics = m.roi != null;
  const subscribers = strategy.subscribers ?? strategy.deployCount ?? 0;
  const creatorName = strategy.creator?.name || (strategy.creatorId ? `User ${strategy.creatorId.slice(-4)}` : 'Creator');

  return (
    <Card className={`flex flex-col border transition-all hover:border-dark-400 cursor-pointer ${selected ? 'border-accent-primary/60 bg-accent-primary/5' : 'border-transparent'}`}
      onClick={() => onDetail(strategy)}>
      {/* Top row */}
      <div className="flex items-start justify-between mb-2" onClick={e => e.stopPropagation()}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="font-semibold text-white text-sm">{strategy.name}</h3>
            <BadgePill badge={strategy.badge} />
            {/* Execution-layer badge — DEX templates show wallet-connected
                pill, CEX templates show exchange-API pill. Helps users at a
                glance know whether deploying needs MetaMask or an exchange
                key without opening the strategy detail drawer. */}
            {(strategy.executionLayer || 'cex') === 'dex' ? (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30" title="Executes via your connected wallet — no exchange API key needed">DEX</span>
            ) : (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30" title="Executes via a CEX (exchange API key needed — see Settings → Exchanges)">CEX</span>
            )}
            {strategy.trackRecord && (
              <TrackRecordBadge trackRecord={strategy.trackRecord} mode="inline" />
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{strategy.category} · {strategy.chain || strategy.pair || '—'}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${risk.bg} ${risk.text}`}>{strategy.risk}</span>
      </div>

      {/* Sparkline + ROI */}
      <div className="flex items-end justify-between mb-3">
        <Sparkline id={strategy.id} roi={m.roi || 0} risk={strategy.risk} />
        <div className="text-right">
          {hasMetrics ? (
            <>
              <p className="text-emerald-400 font-bold text-lg">+{m.roi}%</p>
              <p className="text-gray-500 text-xs">Backtested ROI</p>
            </>
          ) : (
            <>
              <p className="text-gray-500 font-bold text-sm">No data</p>
              <p className="text-gray-600 text-xs">Run backtest</p>
            </>
          )}
        </div>
      </div>

      <p className="text-gray-400 text-xs flex-1 mb-3 line-clamp-2">{strategy.description}</p>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 gap-1.5 mb-3 text-xs">
        <div className="bg-dark-700 rounded p-1.5 text-center">
          <p className="text-gray-500">Win Rate</p>
          <p className="text-accent-primary font-bold">{m.winRate != null ? `${m.winRate}%` : '—'}</p>
        </div>
        <div className="bg-dark-700 rounded p-1.5 text-center">
          <p className="text-gray-500">Max DD</p>
          <p className="text-red-400 font-bold">{m.maxDrawdown != null ? `${m.maxDrawdown}%` : '—'}</p>
        </div>
        <div className="bg-dark-700 rounded p-1.5 text-center">
          <p className="text-gray-500">Sharpe</p>
          <p className="text-chart-purple font-bold">{m.sharpeRatio != null ? m.sharpeRatio : '—'}</p>
        </div>
      </div>

      {/* Indicators */}
      <div className="flex flex-wrap gap-1 mb-3">
        {(strategy.indicators || strategy.tags || []).slice(0, 4).map(t => (
          <span key={t} className="text-xs bg-dark-600 text-gray-400 px-1.5 py-0.5 rounded">{t}</span>
        ))}
      </div>

      {/* Creator + rating */}
      <div className="flex items-center gap-1.5 mb-3">
        <div className="w-5 h-5 rounded-full bg-accent-primary/20 text-accent-primary text-xs flex items-center justify-center">{creatorName.slice(0, 1).toUpperCase()}</div>
        <span className="text-xs text-gray-400">{creatorName}</span>
        {strategy.creator?.verified && <Check className="w-3 h-3 text-accent-primary" />}
        <div className="ml-auto">
          {strategy.rating?.avg != null
            ? <StarRating avg={strategy.rating.avg} count={strategy.rating.count || 0} />
            : <span className="text-xs text-gray-600">No reviews yet</span>
          }
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 mt-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-1 text-xs text-gray-500 flex-1">
          <Users className="w-3 h-3" />{subscribers.toLocaleString()}
        </div>
        <span className="text-sm font-bold text-emerald-400">FREE</span>
        <button
          onClick={e => { e.stopPropagation(); onDeploy(strategy); }}
          className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1"
        >
          <Zap className="w-3 h-3" /> Deploy
        </button>
        <button
          onClick={e => { e.stopPropagation(); onSelect(strategy.id); }}
          className={`text-xs px-2 py-1.5 rounded-lg transition-colors ${selected ? 'bg-accent-primary/20 text-accent-primary' : 'bg-dark-700 text-gray-400 hover:text-white'}`}
        >
          {selected ? '✓' : 'vs'}
        </button>
      </div>
    </Card>
  );
}

function FeaturedBanner({ strategies, onDeploy, onDetail }) {
  const featured = strategies.filter(s => s.featured);
  if (!featured.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {featured.map(s => {
        const risk = RISK_STYLE[s.risk] || RISK_STYLE.Medium;
        const m = s.metrics || {};
        return (
          <div key={s.id}
            className="relative rounded-2xl border border-accent-primary/30 bg-gradient-to-br from-accent-primary/10 via-dark-700 to-dark-800 p-5 cursor-pointer hover:border-accent-primary/60 transition-all"
            onClick={() => onDetail(s)}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-accent-primary font-semibold uppercase tracking-wide">Featured</span>
                  <BadgePill badge={s.badge} />
                </div>
                <h3 className="text-white font-bold text-xl">{s.name}</h3>
                <p className="text-gray-400 text-sm">{s.category} · {s.chain || s.pair || '—'} · {s.tokenPair || s.pair || '—'}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${risk.bg} ${risk.text}`}>{s.risk}</span>
            </div>
            <p className="text-gray-300 text-sm mb-4 line-clamp-2">{s.description}</p>
            <div className="flex gap-4 mb-4">
              <div><p className="text-gray-500 text-xs">Backtested ROI</p><p className="text-emerald-400 font-bold text-xl">{m.roi != null ? `+${m.roi}%` : '—'}</p></div>
              <div><p className="text-gray-500 text-xs">Win Rate</p><p className="text-accent-primary font-bold text-xl">{m.winRate != null ? `${m.winRate}%` : '—'}</p></div>
              <div><p className="text-gray-500 text-xs">Sharpe</p><p className="text-chart-purple font-bold text-xl">{m.sharpeRatio ?? '—'}</p></div>
              <div><p className="text-gray-500 text-xs">Calmar</p><p className="text-blue-400 font-bold text-xl">{m.calmarRatio ?? '—'}</p></div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={e => { e.stopPropagation(); onDeploy(s); }}
                className="btn-primary flex items-center gap-2 px-5 py-2.5"
              >
                <Zap className="w-4 h-4" /> Deploy Now
              </button>
              <span className="text-lg font-bold text-emerald-400">FREE</span>
              <div className="ml-auto flex items-center gap-1 text-sm text-gray-400">
                <Users className="w-4 h-4" />{(s.subscribers ?? s.deployCount ?? 0).toLocaleString()} users
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Publish Strategy Modal
function PublishModal({ prefill, onClose, onPublished }) {
  const VALID_CATEGORIES = ['Trend Following','Mean Reversion','Breakout','Scalping','AI-Based','Grid Trading','Market Making'];
  const [form, setForm] = useState({
    name: prefill?.name || '',
    description: prefill?.description || '',
    category: VALID_CATEGORIES.includes(prefill?.category) ? prefill.category : 'Trend Following',
    risk: ['Low','Medium','High'].includes(prefill?.risk) ? prefill.risk : 'Medium',
    pair: prefill?.pair || 'WETH/USDC',
    tags: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const publish = async () => {
    if (!form.name || !form.description) { setError('Name and description are required.'); return; }
    setSaving(true);
    setError('');
    try {
      const result = await api.publishStrategy({
        name: form.name,
        description: form.description,
        category: form.category,
        risk: form.risk,
        pair: form.pair,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      onPublished(result?.strategy, prefill?.strategyId);
    } catch (err) {
      setError(err?.message || 'Failed to publish. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <Card className="w-full max-w-md mx-4 border border-dark-500">
        <CardHeader title="Publish to Marketplace" subtitle="Your strategy will be listed publicly. You earn 5% of all profits made by bots using it." />
        <div className="space-y-3 mt-4">
          <div>
            <label className="text-gray-400 text-xs mb-1 block">Strategy Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input w-full text-sm" placeholder="e.g. RSI Momentum Pro" />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">Description *</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="input w-full text-sm resize-none" rows={3} placeholder="Describe how the strategy works, when it enters/exits, and what makes it unique." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="input w-full text-sm">
                {['Trend Following','Mean Reversion','Breakout','Scalping','AI-Based','Grid Trading','Market Making'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Risk Level</label>
              <select value={form.risk} onChange={e => setForm(f => ({ ...f, risk: e.target.value }))} className="input w-full text-sm">
                {['Low','Medium','High'].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">Trading Pair</label>
            <input value={form.pair} onChange={e => setForm(f => ({ ...f, pair: e.target.value }))} className="input w-full text-sm" placeholder="WETH/USDC" />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">Tags (comma-separated)</label>
            <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} className="input w-full text-sm" placeholder="rsi, momentum, defi" />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={publish} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {saving ? 'Publishing…' : 'Publish Strategy'}
            </button>
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-dark-700 text-gray-400 hover:text-white text-sm">Cancel</button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function MarketplacePage() {
  const [activeTab, setActiveTab] = useState('Browse');
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  // Execution-layer filter (All / CEX / DEX). Reads from `s.executionLayer`,
  // which is 'cex' for legacy templates (defaulted in the backend) and 'dex'
  // for the new wallet-connected templates that map to /dex-* engines.
  const [executionFilter, setExecutionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [compareIds, setCompareIds] = useState([]);
  const [deployTarget, setDeployTarget] = useState(null);
  const navigate = useNavigate();

  // DEX templates don't fit the CEX "attach strategy to existing bot" deploy
  // modal — each maps to a dedicated DEX engine creation page. Clicking
  // Deploy on a DEX template routes there with `?fromTemplate=<id>` so the
  // destination can highlight the template + (in v1.1) pre-fill the form.
  // CEX templates keep the existing modal flow.
  function handleDeployClick(strategy) {
    if (strategy?.executionLayer === 'dex' && strategy?.deployPath) {
      navigate(`${strategy.deployPath}?fromTemplate=${encodeURIComponent(strategy.id)}`);
      return;
    }
    setDeployTarget(strategy);
  }
  const [detailTarget, setDetailTarget] = useState(null);
  const [creatorData, setCreatorData] = useState(null);
  const [creatorLoading, setCreatorLoading] = useState(false);
  const [creatorError, setCreatorError] = useState('');
  const [showCompare, setShowCompare] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishPrefill, setPublishPrefill] = useState(null);
  // Default ON: show all strategies including newly published / unverified.
  // Safety-conscious users can uncheck to filter to verified-track-record only.
  const [showUnverified, setShowUnverified] = useState(true);

  // My Strategies tab — sourced from Strategy Library (api.listStrategies),
  // the same data Strategy Builder shows. Items are lightweight index entries:
  // { strategyId, name, category, risk, createdAt, updatedAt }.
  const [myStrategies, setMyStrategies] = useState([]);
  const [myStrategiesLoading, setMyStrategiesLoading] = useState(false);
  const [publishedStrategyIds, setPublishedStrategyIds] = useState(() => {
    try {
      const saved = localStorage.getItem('mp_publishedStrategyIds');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  function addPublishedStrategyId(strategyId) {
    setPublishedStrategyIds(prev => {
      const next = new Set([...prev, strategyId]);
      try { localStorage.setItem('mp_publishedStrategyIds', JSON.stringify([...next])); } catch {}
      return next;
    });
  }

  useEffect(() => {
    setLoading(true);
    api.getMarketplaceStrategies()
      .then(d => { if (d?.strategies?.length) setStrategies(d.strategies); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'My Strategies') {
      setMyStrategiesLoading(true);
      Promise.all([
        api.listStrategies().catch(() => ({ strategies: [] })),
        api.getCreatorDashboard().catch(() => ({ strategies: [] })),
      ]).then(([libRes, creatorDash]) => {
        const list = Array.isArray(libRes?.strategies) ? libRes.strategies : [];
        setMyStrategies(list);
        // Names of strategies the current user has already published.
        // (Marketplace publishes create a NEW marketplace record — we match
        // by name back to the library entry to mark it as already-published.)
        const publishedNames = new Set((creatorDash?.strategies || []).map(s => s.name));
        list
          .filter(s => publishedNames.has(s.name))
          .forEach(s => addPublishedStrategyId(s.strategyId));
      }).finally(() => setMyStrategiesLoading(false));
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'Creator Dashboard') {
      setCreatorLoading(true);
      setCreatorError('');
      api.getCreatorDashboard()
        .then(d => setCreatorData(d))
        .catch(err => setCreatorError(err?.message || 'Failed to load creator dashboard.'))
        .finally(() => setCreatorLoading(false));
    }
  }, [activeTab]);

  const sortFn = (a, b) => {
    const am = a.metrics || {}, bm = b.metrics || {};
    switch (sortBy) {
      case 'roi': return (bm.roi || 0) - (am.roi || 0);
      case 'winrate': return (bm.winRate || 0) - (am.winRate || 0);
      case 'sharpe': return (bm.sharpeRatio || 0) - (am.sharpeRatio || 0);
      case 'safe': return (am.maxDrawdown || 0) - (bm.maxDrawdown || 0);
      default: return (b.subscribers || b.deployCount || 0) - (a.subscribers || a.deployCount || 0);
    }
  };

  // Platform templates are curated starter strategies — exempt from the unverified-user filter.
  // The filter exists to hide member-published strategies that lack a verified live track record,
  // not to hide honestly-labeled templates.
  const isPlatformTemplate = (s) => s.template === true || s.trackRecord?.source === 'platform-template' || !s.creatorId;

  const filtered = strategies
    .filter(s => {
      if (category !== 'All' && s.category !== category) return false;
      if (executionFilter !== 'all') {
        // Legacy templates without an explicit executionLayer default to CEX.
        const layer = s.executionLayer || 'cex';
        if (layer !== executionFilter) return false;
      }
      if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !(s.description || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (!showUnverified && !s.trackRecord?.verified && !isPlatformTemplate(s)) return false;
      return true;
    })
    .sort(sortFn);

  const hiddenByVerifiedFilter = !showUnverified
    ? strategies.filter(s => !s.trackRecord?.verified && !isPlatformTemplate(s) && (category === 'All' || s.category === category)).length
    : 0;

  const nonFeatured = filtered.filter(s => !s.featured);
  const compareStrategies = strategies.filter(s => compareIds.includes(s.id));

  const platformStrategies = strategies.filter(s => !s.creatorId);
  const memberStrategies = strategies.filter(s => !!s.creatorId);
  const totalDeployments = strategies.reduce((a, s) => a + (s.subscribers || s.deployCount || 0), 0);
  // Audit only applies to member submissions. Platform templates are hand-curated, not audited.
  const auditedMemberCount = memberStrategies.filter(s => s.auditStatus === 'audited').length;
  const auditStat = memberStrategies.length > 0
    ? `${auditedMemberCount}/${memberStrategies.length}`
    : '—';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary">Strategy Marketplace</h1>
          <p className="text-gray-400">Browse, deploy, and publish algorithmic trading strategies</p>
        </div>
        <div className="flex bg-dark-700 rounded-lg overflow-hidden text-sm">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 transition-colors ${activeTab === tab ? 'bg-accent-primary text-white' : 'text-gray-400 hover:text-white'}`}>
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* ─── BROWSE ─── */}
      {activeTab === 'Browse' && (
        <div className="space-y-5">
          {/* Coming Soon: Creator Marketplace teaser. Builds anticipation for
              the Stripe-Connect-based revenue share feature (see memory
              project_stripe_marketplace_revenue_share.md). Shipped 2026-06-01.
              Remove once the feature actually goes live. */}
          <div className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-5">
            <div className="absolute top-0 right-0 px-3 py-1 bg-amber-500 text-dark-900 text-[10px] font-black uppercase tracking-wider rounded-bl-xl">Coming Soon</div>
            <div className="flex items-start gap-4 pr-24">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-base mb-1">Creator Marketplace — earn from your strategies</h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-2">
                  Soon you'll be able to publish your own algorithmic strategies and earn revenue when other traders deploy them.
                  Profit-share splits direct from Stripe to your bank account. Verified track-record badge included.
                </p>
                <p className="text-gray-400 text-xs">
                  <span className="text-amber-300 font-semibold">For traders:</span> follow proven strategies from real creators with on-chain P&L proof.
                  <span className="text-amber-300 font-semibold ml-2">For creators:</span> 95% revenue share, automated payouts, Pro-tier badge.
                </p>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Platform Strategies', value: platformStrategies.length, color: 'text-accent-primary' },
              { label: 'Member Strategies', value: memberStrategies.length, color: 'text-emerald-400' },
              { label: 'Total Deployments', value: totalDeployments.toLocaleString(), color: 'text-blue-400' },
              { label: 'Audited members', value: auditStat, color: 'text-amber-400' },
            ].map(s => (
              <Card key={s.label} className="py-2 px-3">
                <p className="text-gray-500 text-xs">{s.label}</p>
                <p className={`font-bold text-xl mt-0.5 ${s.color}`}>{s.value}</p>
              </Card>
            ))}
          </div>

          {/* Featured */}
          {/* Backtested disclaimer */}
          <div className="flex items-start gap-2.5 p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl text-xs text-gray-400">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <span><span className="text-amber-400 font-medium">Important:</span> All ROI, win rate, and performance figures are based on backtested simulations against historical data, not live trading results. Past simulated performance does not guarantee future returns. Always conduct your own due diligence.</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32"><Spinner size="lg" className="text-accent-primary" /></div>
          ) : (
            <>
              <FeaturedBanner strategies={filtered} onDeploy={handleDeployClick} onDetail={setDetailTarget} />

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search strategies..." className="input w-full pl-9 text-sm" />
                </div>
                <div className="flex items-center gap-1">
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="input w-auto text-sm">
                    {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <InfoTip side="left">Sort by Win Rate or Sharpe Ratio for sustainable picks. Best ROI ranks raw returns and surfaces high-risk strategies. Lowest Risk filters for safest performers.</InfoTip>
                </div>
              </div>

              {/* Execution-layer chips — All / CEX / DEX. Independent of the
                  category chips below so users can stack (e.g. DEX + Grid). */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[10px] uppercase tracking-wider text-gray-500 mr-1">Layer</span>
                {[
                  { id: 'all', label: 'All' },
                  { id: 'cex', label: 'CEX' },
                  { id: 'dex', label: 'DEX' },
                ].map(opt => (
                  <button key={opt.id} onClick={() => setExecutionFilter(opt.id)}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                      executionFilter === opt.id
                        ? (opt.id === 'dex' ? 'bg-violet-500 text-white' : opt.id === 'cex' ? 'bg-blue-500 text-white' : 'bg-accent-primary text-white')
                        : 'bg-dark-700 text-gray-400 hover:text-white'
                    }`}>
                    {opt.label}
                  </button>
                ))}
                <span className="text-gray-700 mx-1">·</span>
                {CATEGORIES.map(cat => (
                  <button key={cat} onClick={() => setCategory(cat)}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${category === cat ? 'bg-accent-primary text-white' : 'bg-dark-700 text-gray-400 hover:text-white'}`}>
                    {cat}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <Link to="/seller-verify" className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Verify your track record
                  </Link>
                  <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer bg-dark-700 px-3 py-1.5 rounded-full">
                    <input type="checkbox" checked={showUnverified} onChange={e => setShowUnverified(e.target.checked)} className="rounded" />
                    <span>Show unverified</span>
                    {!showUnverified && hiddenByVerifiedFilter > 0 && <span className="text-[10px] text-gray-600">({hiddenByVerifiedFilter} hidden)</span>}
                  </label>
                </div>
              </div>

              {/* Compare banner */}
              {compareIds.length > 0 && (
                <div className="bg-accent-primary/10 border border-accent-primary/30 rounded-xl p-3 flex items-center gap-3 flex-wrap">
                  <span className="text-accent-primary text-sm font-medium">{compareIds.length}/3 selected for comparison</span>
                  {compareIds.length >= 2 && (
                    <button onClick={() => setShowCompare(v => !v)} className="btn-primary text-xs py-1 px-3 flex items-center gap-1">
                      {showCompare ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {showCompare ? 'Hide' : 'Compare'}
                    </button>
                  )}
                  <button onClick={() => setCompareIds([])} className="ml-auto text-gray-400 text-xs hover:text-white">Clear</button>
                </div>
              )}

              {/* Comparison table */}
              {showCompare && compareStrategies.length >= 2 && (
                <Card>
                  <CardHeader title="Side-by-Side Comparison" />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-dark-600">
                          <th className="text-left py-2 pr-4 text-gray-400 font-normal">Metric</th>
                          {compareStrategies.map(s => <th key={s.id} className="text-left py-2 pr-4 text-white font-semibold">{s.name}</th>)}
                        </tr>
                      </thead>
                      <tbody className="text-xs">
                        {[
                          { label: 'ROI', fn: s => s.metrics?.roi != null ? `+${s.metrics.roi}%` : '—', c: () => 'text-emerald-400' },
                          { label: 'Win Rate', fn: s => s.metrics?.winRate != null ? `${s.metrics.winRate}%` : '—', c: () => 'text-accent-primary' },
                          { label: 'Max Drawdown', fn: s => s.metrics?.maxDrawdown != null ? `${s.metrics.maxDrawdown}%` : '—', c: () => 'text-red-400' },
                          { label: 'Sharpe Ratio', fn: s => s.metrics?.sharpeRatio ?? '—', c: () => 'text-chart-purple' },
                          { label: 'Calmar Ratio', fn: s => s.metrics?.calmarRatio ?? '—', c: () => 'text-blue-400' },
                          { label: 'Profit Factor', fn: s => s.metrics?.profitFactor ?? '—', c: () => 'text-amber-400' },
                          { label: 'Risk', fn: s => s.risk || '—', c: s => RISK_STYLE[s.risk]?.text || '' },
                          { label: 'Min Capital', fn: s => s.minCapital != null ? `$${s.minCapital}` : '—', c: () => 'text-gray-300' },
                        ].map(row => (
                          <tr key={row.label} className="border-b border-dark-700">
                            <td className="py-2 pr-4 text-gray-400">{row.label}</td>
                            {compareStrategies.map(s => (
                              <td key={s.id} className={`py-2 pr-4 font-semibold ${row.c(s)}`}>{row.fn(s)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Platform Strategies Grid */}
              {(() => {
                const platformFiltered = nonFeatured.filter(s => !s.creatorId);
                const memberFiltered = nonFeatured.filter(s => !!s.creatorId);
                return (
                  <>
                    {platformFiltered.length > 0 && (
                      <div>
                        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Award className="w-4 h-4 text-accent-primary" /> Platform Strategies
                          <span className="text-xs font-normal text-gray-500 normal-case tracking-normal">— Built and maintained by haythix</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {platformFiltered.map(s => (
                            <StrategyCard key={s.id} strategy={s}
                              onDeploy={handleDeployClick}
                              onDetail={setDetailTarget}
                              selected={compareIds.includes(s.id)}
                              onSelect={id => setCompareIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {memberFiltered.length > 0 && (
                      <div>
                        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4 text-emerald-400" /> Member Strategies
                          <span className="text-xs font-normal text-gray-500 normal-case tracking-normal">— Shared by community members</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {memberFiltered.map(s => (
                            <StrategyCard key={s.id} strategy={s}
                              onDeploy={handleDeployClick}
                              onDetail={setDetailTarget}
                              selected={compareIds.includes(s.id)}
                              onSelect={id => setCompareIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {filtered.length === 0 && (
                      <div className="text-center py-16">
                        <Store className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">No strategies match your filters</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ─── MY STRATEGIES ─── */}
      {activeTab === 'My Strategies' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">Strategies from your <strong className="text-gray-300">Strategy Library</strong>. Publish one to the marketplace so other users can deploy it — you earn <span className="text-accent-primary font-medium">5% of all profits</span> they generate.</p>
            <button onClick={() => { setPublishPrefill(null); setShowPublishModal(true); }} className="btn-primary text-sm flex items-center gap-2 whitespace-nowrap ml-4">
              <Upload className="w-4 h-4" /> Publish New
            </button>
          </div>

          {myStrategiesLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 text-accent-primary animate-spin" />
            </div>
          ) : myStrategies.length === 0 ? (
            <Card className="text-center py-12">
              <Bot className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-white font-medium mb-1">No strategies saved yet</p>
              <p className="text-gray-400 text-sm mb-4">Open <strong>Strategy Builder</strong> to design a strategy, save it to your library, then return here to publish it to the marketplace.</p>
              <div className="flex gap-3 justify-center">
                <a href="/strategy-builder" className="btn-primary text-sm">Open Strategy Builder</a>
                <button onClick={() => setActiveTab('Browse')} className="btn-ghost text-sm">Browse Marketplace</button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myStrategies.map(s => {
                const savedDate = s.updatedAt || s.createdAt;
                const isPublished = publishedStrategyIds.has(s.strategyId);
                return (
                  <Card key={s.strategyId} className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate">{s.name || 'Unnamed Strategy'}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {s.category || 'Custom'}{savedDate ? ` · Saved ${new Date(savedDate).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${(RISK_STYLE[s.risk] || RISK_STYLE.Medium).bg} ${(RISK_STYLE[s.risk] || RISK_STYLE.Medium).text}`}>
                        {s.risk || 'Medium'}
                      </span>
                    </div>

                    {isPublished ? (
                      <div className="mt-auto w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                        <Check className="w-3.5 h-3.5" /> Published to Marketplace
                      </div>
                    ) : (
                      <button
                        onClick={async () => {
                          // Fetch full strategy to prefill description for the publish form.
                          let description = '';
                          try {
                            const full = await api.getStrategy(s.strategyId);
                            description = full?.strategy?.description || '';
                          } catch {}
                          setPublishPrefill({
                            name: s.name,
                            description,
                            category: s.category,
                            risk: s.risk,
                            strategyId: s.strategyId,
                          });
                          setShowPublishModal(true);
                        }}
                        className="mt-auto w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium border border-accent-primary/30 text-accent-primary hover:bg-accent-primary hover:text-dark-900 transition-all"
                      >
                        <Upload className="w-3.5 h-3.5" /> Publish to Marketplace
                      </button>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── CREATOR DASHBOARD ─── */}
      {activeTab === 'Creator Dashboard' && (
        <div className="space-y-6">
          {/* Revenue share explanation */}
          <div className="p-4 bg-accent-primary/5 border border-accent-primary/20 rounded-xl text-sm text-gray-300 leading-relaxed">
            <span className="text-accent-primary font-semibold">How creator earnings work: </span>
            When another user deploys your strategy and their bot makes profit, you automatically receive <span className="text-accent-primary font-semibold">5% of that profit</span>. Earnings are credited to your account in real time as trades settle.
          </div>

          {creatorLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 text-accent-primary animate-spin" />
            </div>
          ) : creatorError ? (
            <Card className="text-center py-10">
              <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-red-400 text-sm">{creatorError}</p>
            </Card>
          ) : creatorData ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Strategy Earnings', value: `$${(creatorData.totalEarnings || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: DollarSign, color: 'text-emerald-400' },
                  { label: 'Total Deployments', value: (creatorData.totalDeploys || 0).toLocaleString(), icon: Users, color: 'text-accent-primary' },
                  { label: 'Profit Generated', value: `$${(creatorData.totalProfitGenerated || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, icon: TrendingUp, color: 'text-chart-purple' },
                  { label: 'Published', value: (creatorData.strategies?.length || 0), icon: Store, color: 'text-amber-400' },
                ].map(stat => (
                  <Card key={stat.label}>
                    <div className="flex items-start justify-between">
                      <p className="text-gray-400 text-xs">{stat.label}</p>
                      <div className={`p-2 rounded-lg bg-dark-600 ${stat.color}`}><stat.icon className="w-4 h-4" /></div>
                    </div>
                    <p className={`text-2xl font-bold mt-2 ${stat.color}`}>{stat.value}</p>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader
                  title="My Published Strategies"
                  action={
                    <button onClick={() => { setPublishPrefill(null); setShowPublishModal(true); }} className="btn-primary text-sm flex items-center gap-2">
                      <Upload className="w-4 h-4" /> Publish New
                    </button>
                  }
                />
                {!creatorData.strategies?.length ? (
                  <div className="text-center py-10">
                    <Store className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm mb-4">No strategies published yet.</p>
                    <p className="text-gray-500 text-xs max-w-sm mx-auto">Go to <strong className="text-gray-300">My Strategies</strong> tab to publish a strategy you built, or click "Publish New" to create one directly.</p>
                  </div>
                ) : (
                  <div className="space-y-3 mt-2">
                    {creatorData.strategies.map(s => (
                      <div key={s.id || s.strategyId} className="flex items-center gap-4 bg-dark-700/50 rounded-xl px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white text-sm truncate">{s.name}</p>
                          <p className="text-xs text-gray-500">{s.category || '—'} · {s.pair || '—'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Deployments</p>
                          <p className="font-bold text-accent-primary">{s.deployCount || 0}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Earnings</p>
                          <p className="font-bold text-emerald-400">${(s.totalCreatorEarnings || 0).toFixed(2)}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${RISK_STYLE[s.risk]?.bg || 'bg-amber-500/20'} ${RISK_STYLE[s.risk]?.text || 'text-amber-400'}`}>
                          {s.risk || 'Medium'}
                        </span>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Unpublish "${s.name}"? It will be removed from the marketplace.`)) return;
                            try {
                              await api.unpublishStrategy(s.id || s.strategyId);
                              setCreatorData(prev => ({
                                ...prev,
                                strategies: prev.strategies.filter(x => (x.id || x.strategyId) !== (s.id || s.strategyId)),
                              }));
                              setStrategies(prev => prev.filter(x => x.id !== (s.id || s.strategyId)));
                              // Clear the published badge for any library strategy with this name
                              setPublishedStrategyIds(prev => {
                                const next = new Set(prev);
                                myStrategies.filter(x => x.name === s.name).forEach(x => next.delete(x.strategyId));
                                try { localStorage.setItem('mp_publishedStrategyIds', JSON.stringify([...next])); } catch {}
                                return next;
                              });
                            } catch (err) {
                              alert(err?.message || 'Failed to unpublish strategy.');
                            }
                          }}
                          title="Unpublish strategy"
                          className="p-1.5 rounded-lg bg-dark-600 text-gray-500 hover:bg-red-500/20 hover:text-red-400 transition-colors flex-shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </>
          ) : null}
        </div>
      )}

      {/* Strategy Detail Drawer */}
      {detailTarget && (
        <StrategyDrawer
          strategy={detailTarget}
          onClose={() => setDetailTarget(null)}
          onDeploy={s => { setDetailTarget(null); handleDeployClick(s); }}
        />
      )}

      {/* Deploy Modal */}
      {deployTarget && (
        <DeployModal strategy={deployTarget} onClose={() => setDeployTarget(null)} />
      )}

      {/* Publish Modal */}
      {showPublishModal && (
        <PublishModal
          prefill={publishPrefill}
          onClose={() => setShowPublishModal(false)}
          onPublished={(strategy, strategyId) => {
            setShowPublishModal(false);
            // Mark the library strategy as published (persists to localStorage)
            if (strategyId) {
              addPublishedStrategyId(strategyId);
            }
            // Refresh creator dashboard
            if (activeTab === 'Creator Dashboard') {
              api.getCreatorDashboard().then(d => setCreatorData(d)).catch(() => {});
            }
            // Add to browse list so it appears immediately
            if (strategy) {
              setStrategies(prev => [strategy, ...prev]);
              setActiveTab('Browse');
            }
          }}
        />
      )}
    </div>
  );
}
